import { baseUrl, httpClient } from '../providers/AppDataProvider';

const API_PATH = `${baseUrl}/vault/params`;

/** A single-value runtime config (C-06 §3). `value` is null exactly when never saved. */
export interface ConfigEntry<T> {
  value: T | null;
  revision: number;
  isSet: boolean;
}

/** Percentages are FRACTIONS on the wire (`0.300000` = 30%); decimals are strings. */
export interface VaultCoreParams {
  minSubscription: string;
  minRedemption: string;
  lockupMinDays: number;
  lockupMaxDays: number;
  performanceFeeCap: string;
  ownershipFloor: string;
  /** Per-user intake limits (XBIT-4659). The read always returns both (`Int!`). The first is
   * fixed at 1 in V1 and shown read-only; a save never carries it. */
  maxApplicationsInFlight: number;
  maxLiveVaultsPerManager: number;
}

export interface VaultRiskParams {
  instrumentWhitelist: string[];
  maxLeverage: string;
  concentrationCap: string;
  marginWarning: string;
  forcedDeleverage: string;
  slippageTolerance: string;
  closeTimeoutSeconds: number;
  confirmCloseThreshold: string;
  /** Venue-clock thresholds (integer ms) read by vault_engine's NAV fence (XBIT-4165).
   * The engine requires all four on every save. */
  eventTimeToleranceMs: number;
  venueLagAllowanceMs: number;
  maxVenueReadSpanMs: number;
  venueTimeSkewBandMs: number;
}

export interface VaultDerivedParams {
  sizeMultiple: string;
  initialNav: string;
  navDecimals: number;
  navDisplayDecimals: number;
  shareDecimals: number;
}

export interface VaultParams {
  core: ConfigEntry<VaultCoreParams>;
  risk: ConfigEntry<VaultRiskParams>;
  derived: VaultDerivedParams | null;
  /** The engine's own defaults (C-06 `vault*ParamsDefaults`) — never a copy kept here. */
  coreDefaults: VaultCoreParams | null;
  riskDefaults: VaultRiskParams | null;
}

function normalizeEntry<T>(raw: unknown): ConfigEntry<T> {
  const source = (raw ?? {}) as { value?: T | null; revision?: number; isSet?: boolean };
  return {
    value: source.value ?? null,
    revision: typeof source.revision === 'number' ? source.revision : 0,
    isSet: source.isSet === true,
  };
}

export async function getVaultParams(): Promise<VaultParams> {
  const { json } = await httpClient(API_PATH, { method: 'GET' });
  const raw = (json ?? {}) as Record<string, unknown>;
  return {
    core: normalizeEntry<VaultCoreParams>(raw.core),
    risk: normalizeEntry<VaultRiskParams>(raw.risk),
    derived: (raw.derived as VaultDerivedParams) ?? null,
    coreDefaults: (raw.coreDefaults as VaultCoreParams) ?? null,
    riskDefaults: (raw.riskDefaults as VaultRiskParams) ?? null,
  };
}

/**
 * Full replace. `expectedRevision` must echo the revision that was READ
 * (0 for a key never saved), never `revision + 1`.
 */
export async function updateVaultCoreParams(
  value: VaultCoreParams,
  expectedRevision: number
): Promise<ConfigEntry<VaultCoreParams>> {
  const { json } = await httpClient(`${API_PATH}/core`, {
    method: 'PUT',
    body: JSON.stringify({ value, expectedRevision }),
  });
  return normalizeEntry<VaultCoreParams>(json);
}

/** Full replace — the whitelist is sent complete; an omitted coin is removed. */
export async function updateVaultRiskParams(
  value: VaultRiskParams,
  expectedRevision: number
): Promise<ConfigEntry<VaultRiskParams>> {
  const { json } = await httpClient(`${API_PATH}/risk`, {
    method: 'PUT',
    body: JSON.stringify({ value, expectedRevision }),
  });
  return normalizeEntry<VaultRiskParams>(json);
}
